import React, { useState, useRef, useEffect } from "react";
import { inject, observer } from "mobx-react";
import i18n from "i18next";
import { withTranslation } from "react-i18next";
import { withRouter } from "react-router-dom";
import { isMobile } from "react-device-detect";
import * as utilities from "../../utilities/formats";
import TextInputSearch from "../../elements/form-input-text-search";
import "./searchInput.css";

const DEBOUNCE = 200;
const MIN_QUERY_LENGTH = 2;

const stripBold = (s) => (s ? s.replace(/<\/?b>/gi, "") : "");
const looksLikeIsbn = (value) => {
  const cleaned = value.replace(/[^0-9X]/gi, "");
  return (
    /^[0-9]{9}[0-9X]$/i.test(cleaned) ||
    /^97[89][0-9]{10}$/.test(cleaned)
  );
};
const cleanIsbn = (value) => value.replace(/[^0-9X]/gi, "");

/* -------------------------
   NORMALIZATION HELPERS
------------------------- */
const normalizeList = (list) =>
  (list || [])
    .flat(Infinity)
    .map((item) => {
      if (typeof item === "string") return item;
      if (Array.isArray(item)) {
        return item.join(" ");
      }
      if (item && typeof item === "object") {
        if (Array.isArray(item.term)) {
          return item.term.join(" ");
        }
        if (typeof item.term === "string") {
          return item.term;
        }
      }
      return "";
    })
    .map((s) => s.trim())
    .filter(Boolean);

const normalizeCompare = (s) =>
  stripBold(s)
    .toLowerCase()
    .replace(/\s+/g, " ")
    .trim();

/* -----------------------------------------------------------
   COMPONENT
----------------------------------------------------------- */
const SearchInput = inject("stores")(
  observer(({ stores, history, position, firstname }) => {
    const storeAuth = stores.storeAuth;
    const storeUi = stores.storeUi;
    const abortRef = useRef(null);
    const debounceRef = useRef(null);
    const [searchValue, setSearchValue] = useState(
      typeof storeUi.search_value === "string" ? storeUi.search_value : ""
    );
    const [highlighted, setHighlighted] = useState(null);
    const [hasError, setHasError] = useState(false);
    const [suggestions, setSuggestions] = useState({
      titles: [],
      authors: [],
      didYouMean: null,
      totalTitles: 0,
      totalAuthors: 0,
    });

    useEffect(() => {
        if (!hasError) return;

        const timer = setTimeout(() => {
          setHasError(false);
        }, 5000);
      
        return () => clearTimeout(timer);
      }, [hasError]);


    const closeAutocomplete = () => {
      setHighlighted(null);
      setSuggestions({
        titles: [],
        authors: [],
        didYouMean: null,
        totalTitles: 0,
        totalAuthors: 0,
      });
      storeUi.search_input_focused = false;
    };

    /* -------------------------
       AUTOCOMPLETE FETCH
    ------------------------- */
    const fetchSuggestions = (query) => {
      if (
        !query ||
        query.length < MIN_QUERY_LENGTH ||
        looksLikeIsbn(query)
      ) {
        closeAutocomplete();
        return;
      }
      if (abortRef.current) {
        abortRef.current.abort();
      }
      const controller = new AbortController();
      abortRef.current = controller;
      fetch(
        process.env.REACT_APP_URL_API +
          "/search/autocomplete-data.php?q=" +
          encodeURIComponent(query),
        { signal: controller.signal }
      )
        .then((res) => res.json())
        .then((data) => {
          const titles = normalizeList(data.titles);
          const authors = normalizeList(data.authors);
          setSuggestions({
            titles: titles.slice(0, 5),
            authors: authors.slice(0, 5),
            didYouMean:
              typeof data.corrected === "string" ? data.corrected : null,
            totalTitles: titles.length,
            totalAuthors: authors.length,
          });
          setHighlighted(null);
        })
        .catch(() => closeAutocomplete());
    };

    /* -------------------------
       INPUT CHANGE
    ------------------------- */
    const handleChange = (e) => {
      const value = e.target.value;
      setSearchValue(value);
      setHasError(false);
      storeUi.search_input_focused = true;
      clearTimeout(debounceRef.current);
      if (looksLikeIsbn(value)) {
        closeAutocomplete();
        return;
      }
      debounceRef.current = setTimeout(
        () => fetchSuggestions(value),
        DEBOUNCE
      );
    };

    /* -------------------------
       SUBMIT
    ------------------------- */
    const onSubmit = (overrideValue) => {
         window.scrollTo(0, 0);
      const raw = String(overrideValue ?? searchValue ?? "").trim();
      const text = stripBold(raw);
      if (!text) {
        setHasError(true);
        return;
      }
      setHasError(false);
      const params = new URLSearchParams();
      if (looksLikeIsbn(text)) {
        params.set("isbn", text);
      } else if (
        suggestions.titles
          .map(normalizeCompare)
          .includes(normalizeCompare(text))
      ) {
        params.set("title", text);
      } else if (
        suggestions.authors
          .map(normalizeCompare)
          .includes(normalizeCompare(text))
      ) {
        params.set("author", text);
      } else {
        params.set("keyword", text);
      }
      history.push("/search-results?" + params.toString());
      closeAutocomplete();
      storeUi.setSearchOverlay(false);
      storeUi.setSearchValue(text);
      setSearchValue(text);
    };

    /* -------------------------
       KEYBOARD HANDLING
    ------------------------- */
    const handleKeyDown = (e) => {
      window.scrollTo(0, 0);
      const input = stripBold(searchValue || "").trim();
      const flat = [...suggestions.titles, ...suggestions.authors];
      if (e.key === "ArrowDown" && flat.length > 0) {
        e.preventDefault();
        setHighlighted((h) => (h === null ? 0 : (h + 1) % flat.length));
        return;
      }
      if (e.key === "ArrowUp" && flat.length > 0) {
        e.preventDefault();
        setHighlighted((h) =>
          h === null ? flat.length - 1 : h > 0 ? h - 1 : flat.length - 1
        );
        return;
      }
      if (e.key === "Enter") {
        e.preventDefault();
        if (!input) {
          setHasError(true);
          return;
        }
        if (looksLikeIsbn(input)) {
          history.push(`/book/${cleanIsbn(input)}`);
          closeAutocomplete();
          storeUi.setSearchOverlay(false);
          return;
        }
        if (highlighted !== null && flat[highlighted]) {
          onSubmit(flat[highlighted]);
          return;
        }
        if (suggestions.didYouMean) {
          onSubmit(suggestions.didYouMean);
          return;
        }
        onSubmit();
      }
    };

    /* -------------------------
       RENDER AUTOCOMPLETE
    ------------------------- */
    const renderGroups = () => {
      if (!storeUi.search_input_focused) return null;
      if (!searchValue.trim()) return null;
      const { titles, authors } = suggestions;
      return (
        <div className="autocomplete-groups">
          {titles.length > 0 && (
            <div className="group">
              <div className="group-title">Titels</div>
              {titles.map((title, idx) => (
                <div
                  key={`title-${idx}`}
                  className={
                    "group-item" +
                    (highlighted === idx ? " is-highlighted" : "")
                  }
                  onMouseDown={(e) => {
                    e.preventDefault();
                    onSubmit(title);
                  }}
                >
                  <span dangerouslySetInnerHTML={{ __html: title }} />
                </div>
              ))}
            </div>
          )}
          {authors.length > 0 && (
            <div className="group">
              <div className="group-title">Auteurs</div>
              {authors.map((author, idx) => {
                const realIdx = titles.length + idx;
                return (
                  <div
                    key={`author-${idx}`}
                    className={
                      "group-item" +
                      (highlighted === realIdx ? " is-highlighted" : "")
                    }
                    onMouseDown={(e) => {
                      e.preventDefault();
                      onSubmit(author);
                    }}
                  >
                    <span dangerouslySetInnerHTML={{ __html: author }} />
                  </div>
                );
              })}
            </div>
          )}
          <div
            className="group-more"
            onMouseDown={(e) => {
              e.preventDefault();
              onSubmit();
            }}
          >
            Vrij zoeken op "{stripBold(searchValue)}"
          </div>
        </div>
      );
    };
    return (

      
      <div
        className={
          "search__input" + (position === "onpage" ? " --onpage" : "")
        }
      >
        <div style={{ position: "relative" }}>
        {searchValue.length > 0 && (
          <div
            className="search-input__clear"
            onClick={() => setSearchValue("")}
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="18"
              height="18"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <line x1="18" y1="6" x2="6" y2="18" />
              <line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          </div>
        )}
          <div
            className="search-input__icon"
            onClick={() => onSubmit()}
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <circle cx="11" cy="11" r="8" />
              <line x1="21" y1="21" x2="16.65" y2="16.65" />
            </svg>
          </div>

          <TextInputSearch
            name="search_input"
            placeholder={
              storeAuth.user.is_loggedin
                ? i18n.t("forms.placeholder.search_input") +
                  ", " +
                  utilities.capitalizeFirstChar(firstname) +
                  "?"
                : i18n.t("forms.placeholder.search_input") + "?"
            }
            value={searchValue}
            onChange={handleChange}
            onKeyDownCapture={handleKeyDown}
            onFocus={() => {
              storeUi.search_input_focused = true;
            }}
            valid={!hasError}
          />

          {hasError && (
            <div className="search-input__error">
              Je moet iets invullen voordat je een zoekopdracht kan uitvoeren.
            </div>
          )}

          {renderGroups()}
        </div>
      </div>
    );
  })
);

export default withTranslation()(withRouter(SearchInput));